import { useState, useMemo, useEffect, useCallback } from "react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Badge } from "@/components/ui/badge"
import {
  Search,
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  Star,
  Copy,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react"
import { toast } from "sonner"
import { formatDate } from "@/lib/utils"

interface Address {
  id: string
  type: "billing" | "shipping" | "other"
  addressLine1: string
  addressLine2?: string
  city: string
  state: string
  postalCode: string
  country: string
  customerId: string
  notes: string
  createdAt: string
  isPrimary: boolean
}

interface AddressesTabProps {
  customerId: string
  customerName: string
  primaryAddress?: string
}

type SortField = "type" | "city" | "state" | "country" | "createdAt"
type SortDirection = "asc" | "desc"

// Address type options
const ADDRESS_TYPE_OPTIONS = ["billing", "shipping", "other"] as const

export function AddressesTab({ customerId, customerName, primaryAddress }: AddressesTabProps) {
  // Initialize with primary address if provided
  const initialAddresses = useMemo(() => {
    const addresses: Address[] = []
    
    if (primaryAddress) {
      // Parse the primary address (assuming format: "Street, City, State ZIP")
      const parts = primaryAddress.split(',').map(p => p.trim())
      addresses.push({
        id: "primary",
        type: "billing",
        addressLine1: parts[0] || primaryAddress,
        addressLine2: undefined,
        city: parts[1] || "",
        state: parts[2]?.split(' ')[0] || "",
        postalCode: parts[2]?.split(' ')[1] || "",
        country: "USA",
        customerId,
        notes: "Primary address from customer record",
        createdAt: new Date().toISOString(),
        isPrimary: true,
      })
    }
    
    return addresses
  }, [customerId, primaryAddress])

  const [addresses, setAddresses] = useState<Address[]>(initialAddresses)
  const [searchQuery, setSearchQuery] = useState("")
  const [sortField, setSortField] = useState<SortField>("type")
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc")

  // Load addresses from localStorage on mount
  useEffect(() => {
    const storedAddresses = localStorage.getItem(`addresses_${customerId}`)
    if (storedAddresses) {
      try {
        const parsed = JSON.parse(storedAddresses)
        setAddresses(parsed)
      } catch (error) {
        console.error("Failed to parse stored addresses:", error)
      }
    }
  }, [customerId])

  // Save addresses to localStorage whenever they change
  useEffect(() => {
    if (addresses.length > 0) {
      localStorage.setItem(`addresses_${customerId}`, JSON.stringify(addresses))
    }
  }, [addresses, customerId])

  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null)

  // Form states
  const [formData, setFormData] = useState({
    type: "billing" as Address["type"],
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
    country: "USA",
    notes: "",
    isPrimary: false,
  })

  // Filter addresses by customer and search query
  const filteredAddresses = useMemo(() => {
    let filtered = addresses.filter((address) => address.customerId === customerId)

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        (address) =>
          address.type.toLowerCase().includes(query) ||
          address.addressLine1.toLowerCase().includes(query) ||
          (address.addressLine2 && address.addressLine2.toLowerCase().includes(query)) ||
          address.city.toLowerCase().includes(query) ||
          address.state.toLowerCase().includes(query) ||
          address.postalCode.includes(query) ||
          address.country.toLowerCase().includes(query)
      )
    }

    // Sort
    filtered.sort((a, b) => {
      let aValue = a[sortField]
      let bValue = b[sortField]

      if (typeof aValue === "string") aValue = aValue.toLowerCase()
      if (typeof bValue === "string") bValue = bValue.toLowerCase()

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1
      return 0
    })

    return filtered
  }, [addresses, customerId, searchQuery, sortField, sortDirection])

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc")
    } else {
      setSortField(field)
      setSortDirection("asc")
    }
  }

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return <ArrowUpDown className="ml-2 h-4 w-4" />
    return sortDirection === "asc" ? (
      <ArrowUp className="ml-2 h-4 w-4" />
    ) : (
      <ArrowDown className="ml-2 h-4 w-4" />
    )
  }

  const handleAddAddress = () => {
    // Validation
    if (!formData.addressLine1 || !formData.city || !formData.state || !formData.postalCode || !formData.country) {
      toast.error("Please fill in all required fields")
      return
    }

    const newAddress: Address = {
      id: `addr_${Date.now()}`,
      type: formData.type,
      addressLine1: formData.addressLine1,
      addressLine2: formData.addressLine2 || undefined,
      city: formData.city,
      state: formData.state,
      postalCode: formData.postalCode,
      country: formData.country,
      customerId,
      notes: formData.notes,
      createdAt: new Date().toISOString(),
      isPrimary: formData.isPrimary,
    }

    // If setting as primary, unset other primary addresses
    if (formData.isPrimary) {
      setAddresses((prev) =>
        [...prev.map((addr) => ({ ...addr, isPrimary: false })), newAddress]
      )
    } else {
      setAddresses((prev) => [...prev, newAddress])
    }

    setIsAddDialogOpen(false)
    resetForm()
    toast.success("Address added successfully")
  }

  const handleEditAddress = () => {
    if (!selectedAddress) return

    // Validation
    if (!formData.addressLine1 || !formData.city || !formData.state || !formData.postalCode || !formData.country) {
      toast.error("Please fill in all required fields")
      return
    }

    const updatedAddress: Address = {
      ...selectedAddress,
      type: formData.type,
      addressLine1: formData.addressLine1,
      addressLine2: formData.addressLine2 || undefined,
      city: formData.city,
      state: formData.state,
      postalCode: formData.postalCode,
      country: formData.country,
      notes: formData.notes,
      isPrimary: formData.isPrimary,
    }

    // If setting as primary, unset other primary addresses
    if (formData.isPrimary) {
      setAddresses((prev) =>
        prev.map((addr) =>
          addr.id === selectedAddress.id
            ? updatedAddress
            : { ...addr, isPrimary: false }
        )
      )
    } else {
      setAddresses((prev) =>
        prev.map((addr) => (addr.id === selectedAddress.id ? updatedAddress : addr))
      )
    }

    setIsEditDialogOpen(false)
    setSelectedAddress(null)
    resetForm()
    toast.success("Address updated successfully")
  }

  const handleDeleteAddress = () => {
    if (!selectedAddress) return

    setAddresses((prev) => prev.filter((addr) => addr.id !== selectedAddress.id))
    setIsDeleteDialogOpen(false)
    setSelectedAddress(null)
    toast.success("Address deleted successfully")
  }

  const handleSetPrimary = (address: Address) => {
    setAddresses((prev) =>
      prev.map((addr) => ({
        ...addr,
        isPrimary: addr.id === address.id,
      }))
    )
    toast.success("Primary address updated")
  }

  const handleCopyAddress = (address: Address) => {
    const fullAddress = [
      address.addressLine1,
      address.addressLine2,
      `${address.city}, ${address.state} ${address.postalCode}`,
      address.country,
    ]
      .filter(Boolean)
      .join("\n")
    
    navigator.clipboard.writeText(fullAddress)
    toast.success("Address copied to clipboard")
  }

  const openAddDialog = () => {
    resetForm()
    setIsAddDialogOpen(true)
  }

  const openEditDialog = (address: Address) => {
    setSelectedAddress(address)
    setFormData({
      type: address.type,
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2 || "",
      city: address.city,
      state: address.state,
      postalCode: address.postalCode,
      country: address.country,
      notes: address.notes,
      isPrimary: address.isPrimary,
    })
    setIsEditDialogOpen(true)
  }

  const openDeleteDialog = (address: Address) => {
    setSelectedAddress(address)
    setIsDeleteDialogOpen(true)
  }

  const resetForm = () => {
    setFormData({
      type: "billing",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      postalCode: "",
      country: "USA",
      notes: "",
      isPrimary: false,
    })
  }

  const getAddressTypeLabel = (type: Address["type"]) => {
    return type.charAt(0).toUpperCase() + type.slice(1)
  }

  const getAddressTypeBadgeVariant = (type: Address["type"]) => {
    switch (type) {
      case "billing":
        return "default"
      case "shipping":
        return "secondary"
      case "other":
        return "outline"
      default:
        return "default"
    }
  }

  return (
    <div className="space-y-4">
      {/* Header with Search and Add Button */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search addresses..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button onClick={openAddDialog}>
          <Plus className="mr-2 h-4 w-4" />
          Add Address
        </Button>
      </div>

      {/* Addresses Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50px]"></TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort("type")}
                  className="h-8 px-2 lg:px-3"
                >
                  Type
                  {getSortIcon("type")}
                </Button>
              </TableHead>
              <TableHead>Address</TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort("city")}
                  className="h-8 px-2 lg:px-3"
                >
                  Town
                  {getSortIcon("city")}
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort("state")}
                  className="h-8 px-2 lg:px-3"
                >
                  County
                  {getSortIcon("state")}
                </Button>
              </TableHead>
              <TableHead>Postcode</TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort("country")}
                  className="h-8 px-2 lg:px-3"
                >
                  Country
                  {getSortIcon("country")}
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort("createdAt")}
                  className="h-8 px-2 lg:px-3"
                >
                  Created
                  {getSortIcon("createdAt")}
                </Button>
              </TableHead>
              <TableHead className="w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAddresses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-muted-foreground">
                  No addresses found
                </TableCell>
              </TableRow>
            ) : (
              filteredAddresses.map((address) => (
                <TableRow key={address.id}>
                  <TableCell>
                    {address.isPrimary && (
                      <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge variant={getAddressTypeBadgeVariant(address.type)}>
                        {getAddressTypeLabel(address.type)}
                      </Badge>
                      {address.isPrimary && (
                        <Badge variant="secondary" className="text-xs">
                          Primary
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="max-w-[250px]">
                    <div className="space-y-0.5">
                      <div className="font-medium">{address.addressLine1}</div>
                      {address.addressLine2 && (
                        <div className="text-sm text-muted-foreground">{address.addressLine2}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{address.city}</TableCell>
                  <TableCell>{address.state}</TableCell>
                  <TableCell>{address.postalCode}</TableCell>
                  <TableCell>{address.country}</TableCell>
                  <TableCell>{formatDate(address.createdAt)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!address.isPrimary && (
                          <DropdownMenuItem onClick={() => handleSetPrimary(address)}>
                            <Star className="mr-2 h-4 w-4" />
                            Set as Primary
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => handleCopyAddress(address)}>
                          <Copy className="mr-2 h-4 w-4" />
                          Copy Address
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEditDialog(address)}>
                          <Edit className="mr-2 h-4 w-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => openDeleteDialog(address)}
                          className="text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add Address Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add New Address</DialogTitle>
            <DialogDescription>
              Add a new address for {customerName}. Fields marked with * are required.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="add-type">
                Address Type <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value as Address["type"] })}
              >
                <SelectTrigger id="add-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {ADDRESS_TYPE_OPTIONS.map((type) => (
                    <SelectItem key={type} value={type}>
                      {getAddressTypeLabel(type)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-addressLine1">
                Address Line 1 <span className="text-destructive">*</span>
              </Label>
              <Input
                id="add-addressLine1"
                value={formData.addressLine1}
                onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                placeholder="123 Main Street"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-addressLine2">Address Line 2</Label>
              <Input
                id="add-addressLine2"
                value={formData.addressLine2}
                onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                placeholder="Suite 100 (optional)"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="add-city">
                  Town <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="add-city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="New York"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-state">
                  County <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="add-state"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="NY"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="add-postalCode">
                  Postcode <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="add-postalCode"
                  value={formData.postalCode}
                  onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                  placeholder="10001"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="add-country">
                  Country <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="add-country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="USA"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-notes">Notes</Label>
              <Textarea
                id="add-notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes about this address..."
                rows={3}
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="add-primary"
                checked={formData.isPrimary}
                onChange={(e) => setFormData({ ...formData, isPrimary: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="add-primary" className="cursor-pointer">
                Set as primary address
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddAddress}>Add Address</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Address Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Address</DialogTitle>
            <DialogDescription>
              Update address information. Fields marked with * are required.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-type">
                Address Type <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value as Address["type"] })}
              >
                <SelectTrigger id="edit-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {ADDRESS_TYPE_OPTIONS.map((type) => (
                    <SelectItem key={type} value={type}>
                      {getAddressTypeLabel(type)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-addressLine1">
                Address Line 1 <span className="text-destructive">*</span>
              </Label>
              <Input
                id="edit-addressLine1"
                value={formData.addressLine1}
                onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                placeholder="123 Main Street"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-addressLine2">Address Line 2</Label>
              <Input
                id="edit-addressLine2"
                value={formData.addressLine2}
                onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                placeholder="Suite 100 (optional)"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-city">
                  Town <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="edit-city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="New York"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-state">
                  County <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="edit-state"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="NY"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-postalCode">
                  Postcode <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="edit-postalCode"
                  value={formData.postalCode}
                  onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                  placeholder="10001"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-country">
                  Country <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="edit-country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="USA"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Textarea
                id="edit-notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes about this address..."
                rows={3}
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="edit-primary"
                checked={formData.isPrimary}
                onChange={(e) => setFormData({ ...formData, isPrimary: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="edit-primary" className="cursor-pointer">
                Set as primary address
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditAddress}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Address</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this address? This action cannot be undone.
              {selectedAddress?.isPrimary && (
                <span className="block mt-2 text-amber-600 dark:text-amber-500">
                  This is the primary address. The next address will be automatically promoted to
                  primary.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteAddress}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
